import i18next from 'i18next'
import { saveFullUser } from './saveUserAction'
import { createUser } from './createUserApi'
import {
  fetchUser,
  getFamilyRelations,
  getAssignTags,
  getOneHousehold,
  assignUser,
} from './usersApi'
import { buildUserFormData } from './buildUserFormData'
import api from 'service/api'

// --------------- Row shapes ---------------
// Produced by ImportUsersModal's column mapping — fields absent from a given
// file template simply come through as `undefined` on the row object.
export type ImportUserRow = {
  givenName: string
  familyName: string
  middleName: string
  nickName: string
  dateOfBirth: string
  israeliId: string
  gender: string
  maritalStatus: string
  mainMobilePhone: string
  secondaryMobilePhone: string
  otherPhone: string
  email: string
  addressCity: string
  addressStreet: string
  addressHouseNumber: string
  shelter: string
  contactPerson1: string
  contactPersonCellPhone1: string
  contactPerson2: string
  contactPersonCellPhone2: string
  tagsOthers: string
  tagsRoles: string
  tagsStatus: string
  isLocalMember: string
}

export type ImportChildRow = {
  parentIdentityNumber: string
  israeliId: string
  givenName: string
  familyName: string
  middleName: string
  mainMobilePhone: string
  dateOfBirth: string
  gender: string
}

export type RowStatus = 'success' | 'error'
type ProgressCb = (index: number, total: number, status: RowStatus, message?: string, detail?: string, code?: string) => void
type LogCb = (message: string) => void

// --------------- Helpers ---------------

const splitTags = (v?: string): string[] => v ? v.split('#').map(s => s.trim()).filter(Boolean) : []

const toIsoBirthDate = (dateOfBirth?: string): string | null => {
  if (!dateOfBirth) return null
  const [day, month, year] = dateOfBirth.split('/')
  if (!day || !month || !year) return null
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00Z`
}

// A date the server will refuse is worth catching here, where the row can say
// which cell is wrong. A digit lost from an identity number lands in the year -
// 22/05/3345 - and the row would otherwise fail with nothing to point at.
const isValidBirthDate = (dateOfBirth?: string): boolean => {
  if (!dateOfBirth) return true

  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(dateOfBirth.trim())
  if (!match) return false

  const [, d, m, y] = match
  const day = Number(d), month = Number(m), year = Number(y)
  const date = new Date(Date.UTC(year, month - 1, day))

  // Round-tripping catches a day that does not exist in that month, which
  // Date would otherwise roll over into the next one.
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return false
  }

  return year >= 1900 && date.getTime() <= Date.now()
}

const mergeField = (rowValue: any, currentValue: any) =>
  (rowValue !== undefined && rowValue !== '') ? rowValue : currentValue

const findUserIdByIdentity = (allUsers: any[], identityNumber?: string): number | null => {
  if (!identityNumber) return null
  const match = (allUsers || []).find((u: any) => u.israelId === identityNumber)
  return match ? (match.userId ?? match.id) : null
}

// Builds a brand-new-user form from a row — used for the "create" template
// and for child rows whose identity number isn't found in the system yet.
const rowToForm = (row: any) => ({
  id: 0,
  population: {
    firstName: row.givenName || '',
    lastName: row.familyName || '',
    middleName: row.middleName || '',
    nickName: row.nickName || '',
    identityNumber: row.israeliId || '',
    birthDate: toIsoBirthDate(row.dateOfBirth),
    gender: row.gender || '',
    maritalStatus: row.maritalStatus || '',
    phone: row.mainMobilePhone || '',
    extraMobile: row.secondaryMobilePhone || '',
    extraPhone: row.otherPhone || '',
    email: row.email || '',
    city: row.addressCity || '',
    street: row.addressStreet || '',
    houseNumber: row.addressHouseNumber || '',
    shelter: row.shelter || '',
    userTypes: splitTags(row.tagsStatus).map(tagName => ({ tagName, tagTypeId: 1, action: 'fromList' })),
    isDead: false,
    isLocalMember: row.isLocalMember === 'כן',
    deathDate: null,
  },
  emergency: {
    contacts: [
      ...(row.contactPerson1 || row.contactPersonCellPhone1 ? [{ mode: 'manual', name: row.contactPerson1, phone: row.contactPersonCellPhone1, address: '' }] : []),
      ...(row.contactPerson2 || row.contactPersonCellPhone2 ? [{ mode: 'manual', name: row.contactPerson2, phone: row.contactPersonCellPhone2, address: '' }] : []),
    ],
  },
  family: {
    household: null,
    spouse: null,
    children: [] as any[],
  },
  contacts: {
    roles: splitTags(row.tagsRoles).map(tagName => ({ tagName, tagTypeId: 2, action: 'fromList' })),
    tags: splitTags(row.tagsOthers).map(tagName => ({ tagName, tagTypeId: 3, action: 'fromList' })),
    groups: [] as any[],
    permissions: {},
  },
})

// Builds an update form for an existing user: starts from their current,
// full server state (so unmentioned fields are preserved on the PUT) and
// overlays only the fields present in the imported row. New tags are
// appended to (not replacing) whatever tags the user already has.
const buildMergedForm = (row: any, currentUser: any, family: any, tags: any, household: any) => {
  const base = buildUserFormData(currentUser, family, tags, household)

  base.population.firstName = mergeField(row.givenName, base.population.firstName)
  base.population.lastName = mergeField(row.familyName, base.population.lastName)
  base.population.middleName = mergeField(row.middleName, base.population.middleName)
  base.population.nickName = mergeField(row.nickName, base.population.nickName)
  base.population.gender = mergeField(row.gender, base.population.gender)
  base.population.maritalStatus = mergeField(row.maritalStatus, base.population.maritalStatus)
  base.population.phone = mergeField(row.mainMobilePhone, base.population.phone)
  base.population.extraMobile = mergeField(row.secondaryMobilePhone, base.population.extraMobile)
  base.population.extraPhone = mergeField(row.otherPhone, base.population.extraPhone)
  base.population.email = mergeField(row.email, base.population.email)
  base.population.city = mergeField(row.addressCity, base.population.city)
  base.population.street = mergeField(row.addressStreet, base.population.street)
  base.population.houseNumber = mergeField(row.addressHouseNumber, base.population.houseNumber)
  if (row.dateOfBirth) base.population.birthDate = toIsoBirthDate(row.dateOfBirth)
  if (row.isLocalMember !== undefined && row.isLocalMember !== '') {
    base.population.isLocalMember = row.isLocalMember === 'כן'
  }

  const existingContact1: any = base.emergency.contacts[0]
  const existingContact2: any = base.emergency.contacts[1]
  const contact1Name = mergeField(row.contactPerson1, existingContact1?.name)
  const contact1Phone = mergeField(row.contactPersonCellPhone1, existingContact1?.phone)
  const contact2Name = mergeField(row.contactPerson2, existingContact2?.name)
  const contact2Phone = mergeField(row.contactPersonCellPhone2, existingContact2?.phone)
  base.emergency.contacts = [
    ...(contact1Name || contact1Phone ? [{ mode: 'manual', name: contact1Name, phone: contact1Phone, address: '' }] : []),
    ...(contact2Name || contact2Phone ? [{ mode: 'manual', name: contact2Name, phone: contact2Phone, address: '' }] : []),
  ]

  const appendTags = (existing: any[], newNames: string[], tagTypeId: number) => {
    const names = new Set(existing.map(t => t.tagName))
    const additions = newNames.filter(n => !names.has(n)).map(tagName => ({ tagName, tagTypeId, action: 'fromList' }))
    return [...existing, ...additions]
  }
  base.population.userTypes = appendTags(base.population.userTypes, splitTags(row.tagsStatus), 1)
  base.contacts.roles = appendTags(base.contacts.roles, splitTags(row.tagsRoles), 2)
  base.contacts.tags = appendTags(base.contacts.tags, splitTags(row.tagsOthers), 3)

  return base
}

const MISSING_CONTACT_CODES: Record<string, string> = {
  MissingMekomeContact: i18next.t('population:err_missing_contact'),
  InvalidIsraeliID: i18next.t('population:err_invalid_id'),
  InvalidPrimaryPhone: i18next.t('population:err_invalid_phone'),
  InvalidContactPhone1: i18next.t('population:err_invalid_contact_phone'),
}

const parseImportError = (err: any): { message: string; detail?: string; code?: string } => {

  if (err?.data?.status === 409 && err?.data?.data?.code === 'DUPLICATE_USER') {
    const u = err.data?.data.existingUser
    const lines: string[] = []
    const nameParts = [u?.existingGivenName, u?.existingFamilyName].filter(Boolean)

    if (nameParts.length) lines.push(i18next.t('population:dup_name', { name: nameParts.join(' ') }))
    if (u?.existingIsraeliId) lines.push(i18next.t('population:dup_id', { id: u.existingIsraeliId }))
    if (u?.existingMainMobile) lines.push(i18next.t('population:dup_mobile', { phone: u.existingMainMobile }))
    if (u?.existingEmail) lines.push(i18next.t('population:dup_email', { email: u.existingEmail }))

    return { message: i18next.t('population:dup_user_exists'), detail: lines.join('\n'), code: 'DUPLICATE_USER' }
  }

  if (err?.data?.status == 400) {
    const apiError = err?.data?.data?.errors.length > 0 ? err?.data?.data?.errors[0] : null
    return {
      message: apiError ? apiError.errorKey : i18next.t('population:unknown'),
      detail: apiError ? apiError.errorDescription : '',
      code: apiError?.errorKey,
    }
  }

  // The status can arrive at any of these three depths depending on which layer
  // wrapped the response, so take the first one that is actually a 422.
  const status422 = err?.data?.status === 422 || err?.code?.status === 422 || err?.status === 422
  if (status422) {
    const code = err?.data?.data?.code ?? err?.data?.code ?? err?.code

    return {
      message: MISSING_CONTACT_CODES[code] ?? i18next.t('population:validation_error', { code: code || i18next.t('population:unknown') }),
      detail: err?.data?.data?.message ?? err?.data?.message ?? '',
      code,
    }
  }

  return {
    message: i18next.t('population:generic_error'),
    detail: ''
  }
}

const isSaveSuccess = (res: any) => res === true || (res && res?.data?.status == 200)

// When a create hits 409 DUPLICATE_USER, mirrors UserNewModal's handleUseExisting:
// if the existing user isn't already a member of this community, relate them to
// it instead of failing the row; if they're already a member, it's a real duplicate.
const isDuplicateUserResponse = (res: any) =>
  res?.data?.status === 409 && res?.data?.data?.code === 'DUPLICATE_USER'

type DuplicateResolution = 'already-member' | 'linked' | 'link-failed'

const resolveDuplicateUser = async (res: any, communityId: number): Promise<{ resolution: DuplicateResolution; existingUserId?: number }> => {
  const data = res?.data?.data
  const existingUserId = data?.existingUser?.existingUserId
  const communityIds: number[] = data?.fullProfile?.communityIds ?? []

  if (communityIds.includes(communityId)) {
    return { resolution: 'already-member', existingUserId }
  }

  const relateRes = await api.relateUserToCommunity(communityId, existingUserId)
  return { resolution: relateRes ? 'linked' : 'link-failed', existingUserId }
}

// --------------- Create (template 1) ---------------

export const importCreateUsers = async (
  rows: ImportUserRow[],
  communityId: number,
  onProgress?: ProgressCb,
  onLog?: LogCb
): Promise<{ success: boolean; imported: number }> => {
  let imported = 0
  let hasImportErrors = false

  for (let i = 0; i < rows.length; i++) {
    const row: any = rows[i]
    const rowLabel = i18next.t('population:row_label', { row: i + 1 })
    try {
      if (!isValidBirthDate(row.dateOfBirth)) {
        onLog?.(i18next.t('population:log_invalid_birth_date', { row: rowLabel, date: row.dateOfBirth }))
        onProgress?.(i, rows.length, 'error', i18next.t('population:err_invalid_birth_date'), i18next.t('population:detail_birth_date', { date: row.dateOfBirth }), 'INVALID_BIRTH_DATE')
        hasImportErrors = true
        continue
      }

      onLog?.(i18next.t('population:log_creating_user', { row: rowLabel, name: `${row.givenName} ${row.familyName}`, id: row.israeliId }))
      const res = await saveFullUser(rowToForm(row), communityId)

      if (isSaveSuccess(res)) {
        imported++
        onLog?.(i18next.t('population:log_created', { row: rowLabel }))
        onProgress?.(i, rows.length, 'success')
      } else if (isDuplicateUserResponse(res)) {
        const dup = await resolveDuplicateUser(res, communityId)
        if (dup.resolution === 'linked') {
          imported++
          onLog?.(i18next.t('population:log_linking_existing', { row: rowLabel, id: dup.existingUserId }))
          onProgress?.(i, rows.length, 'success')
        } else {
          const { message, detail } = dup.resolution === 'link-failed'
            ? { message: i18next.t('population:err_link_existing'), detail: '' }
            : parseImportError(res)
          onLog?.(i18next.t('population:log_error', { row: rowLabel, message }))
          onProgress?.(i, rows.length, 'error', message, detail, dup.resolution === 'link-failed' ? 'RELATE_FAILED' : 'DUPLICATE_USER')
          hasImportErrors = true
        }
      } else {
        const { message, detail, code } = parseImportError(res)
        onLog?.(i18next.t('population:log_error', { row: rowLabel, message }))
        onProgress?.(i, rows.length, 'error', message, detail, code)
        hasImportErrors = true
      }
    } catch (err) {
      const { message, detail, code } = parseImportError(err)
      onLog?.(i18next.t('population:log_error', { row: rowLabel, message }))
      onProgress?.(i, rows.length, 'error', message, detail, code)
      hasImportErrors = true
    }
  }

  return { success: !hasImportErrors, imported }
}

// --------------- Update existing users (template 2) ---------------

export const importUpdateUsers = async (
  rows: ImportUserRow[],
  communityId: number,
  allUsers: any[],
  onProgress?: ProgressCb,
  onLog?: LogCb
): Promise<{ success: boolean; imported: number }> => {
  let imported = 0
  let hasImportErrors = false

  for (let i = 0; i < rows.length; i++) {
    const row: any = rows[i]
    const rowLabel = i18next.t('population:row_label', { row: i + 1 })
    try {
      if (!isValidBirthDate(row.dateOfBirth)) {
        onLog?.(i18next.t('population:log_invalid_birth_date', { row: rowLabel, date: row.dateOfBirth }))
        onProgress?.(i, rows.length, 'error', i18next.t('population:err_invalid_birth_date'), i18next.t('population:detail_birth_date', { date: row.dateOfBirth }), 'INVALID_BIRTH_DATE')
        hasImportErrors = true
        continue
      }

      onLog?.(i18next.t('population:log_looking_up_user', { row: rowLabel, id: row.israeliId }))
      const existingId = findUserIdByIdentity(allUsers, row.israeliId)
      if (!existingId) {
        onLog?.(i18next.t('population:log_user_not_found', { row: rowLabel }))
        onProgress?.(i, rows.length, 'error', i18next.t('population:err_user_not_found'), row.israeliId ? i18next.t('population:detail_id', { id: row.israeliId }) : '', 'USER_NOT_FOUND')
        hasImportErrors = true
        continue
      }
      onLog?.(i18next.t('population:log_user_found', { row: rowLabel, id: existingId }))

      const [userRes, family, tags, household] = await Promise.all([
        fetchUser(existingId, communityId),
        getFamilyRelations(existingId, communityId),
        getAssignTags(existingId, communityId),
        getOneHousehold(existingId, communityId),
      ])

      onLog?.(i18next.t('population:log_saving_update', { row: rowLabel }))
      const form = buildMergedForm(row, userRes.data, family, tags, household?.houseHold)
      const res = await saveFullUser(form, communityId)

      if (isSaveSuccess(res)) {
        imported++
        onLog?.(i18next.t('population:log_updated', { row: rowLabel }))
        onProgress?.(i, rows.length, 'success')
      } else {
        const { message, detail, code } = parseImportError(res)
        onLog?.(i18next.t('population:log_error', { row: rowLabel, message }))
        onProgress?.(i, rows.length, 'error', message, detail, code)
        hasImportErrors = true
      }
    } catch (err) {
      const { message, detail, code } = parseImportError(err)
      onLog?.(i18next.t('population:log_error', { row: rowLabel, message }))
      onProgress?.(i, rows.length, 'error', message, detail, code)
      hasImportErrors = true
    }
  }

  return { success: !hasImportErrors, imported }
}

// --------------- Update children + link parent (template 3) ---------------

export const importChildUsers = async (
  rows: ImportChildRow[],
  communityId: number,
  allUsers: any[],
  onProgress?: ProgressCb,
  onLog?: LogCb
): Promise<{ success: boolean; imported: number }> => {
  let imported = 0
  let hasImportErrors = false

  for (let i = 0; i < rows.length; i++) {
    const row: any = rows[i]
    const rowLabel = i18next.t('population:row_label', { row: i + 1 })

    try {
      if (!row.parentIdentityNumber) {
        onLog?.(i18next.t('population:log_missing_parent_id', { row: rowLabel }))
        onProgress?.(i, rows.length, 'error', i18next.t('population:err_missing_parent_id'), '', 'MISSING_PARENT_ID')
        hasImportErrors = true
        continue
      }
      if (!row.israeliId) {
        onLog?.(i18next.t('population:log_missing_child_id', { row: rowLabel }))
        onProgress?.(i, rows.length, 'error', i18next.t('population:err_missing_child_id'), '', 'MISSING_CHILD_ID')
        hasImportErrors = true
        continue
      }

      if (!isValidBirthDate(row.dateOfBirth)) {
        onLog?.(i18next.t('population:log_invalid_birth_date', { row: rowLabel, date: row.dateOfBirth }))
        onProgress?.(i, rows.length, 'error', i18next.t('population:err_invalid_birth_date'), i18next.t('population:detail_birth_date', { date: row.dateOfBirth }), 'INVALID_BIRTH_DATE')
        hasImportErrors = true
        continue
      }

      onLog?.(i18next.t('population:log_looking_up_parent', { row: rowLabel, id: row.parentIdentityNumber }))
      const parentId = findUserIdByIdentity(allUsers, row.parentIdentityNumber)
      if (!parentId) {
        onLog?.(i18next.t('population:log_parent_not_found', { row: rowLabel }))
        onProgress?.(i, rows.length, 'error', i18next.t('population:err_parent_not_found'), i18next.t('population:detail_parent_id', { id: row.parentIdentityNumber }), 'PARENT_NOT_FOUND')
        hasImportErrors = true
        continue
      }
      onLog?.(i18next.t('population:log_parent_found', { row: rowLabel, id: parentId }))

      onLog?.(i18next.t('population:log_looking_up_child', { row: rowLabel, id: row.israeliId }))
      const existingChildId = findUserIdByIdentity(allUsers, row.israeliId)
      let childId: number

      if (existingChildId) {
        onLog?.(i18next.t('population:log_child_exists', { row: rowLabel, id: existingChildId }))
        const [userRes, family, tags, household] = await Promise.all([
          fetchUser(existingChildId, communityId),
          getFamilyRelations(existingChildId, communityId),
          getAssignTags(existingChildId, communityId),
          getOneHousehold(existingChildId, communityId),
        ])

        const form = buildMergedForm(row, userRes.data, family, tags, household?.houseHold)
        form.population.maritalStatus = ''
        const res = await saveFullUser(form, communityId)

        if (!isSaveSuccess(res)) {
          const { message, detail, code } = parseImportError(res)
          onLog?.(i18next.t('population:log_error', { row: rowLabel, message }))
          onProgress?.(i, rows.length, 'error', message, detail, code)
          hasImportErrors = true
          continue
        }

        childId = existingChildId
      } else {
        onLog?.(i18next.t('population:log_child_not_found', { row: rowLabel }))
        const res: any = await createUser(rowToForm(row), communityId)

        if (isDuplicateUserResponse(res)) {
          const dup = await resolveDuplicateUser(res, communityId)
          // The child already exists in the system: don't create a new user,
          // just reuse the existing one and associate it to the parent below.
          // 'linked'         => existing user was related to this community now.
          // 'already-member' => existing user was already in this community.
          if ((dup.resolution === 'linked' || dup.resolution === 'already-member') && dup.existingUserId) {
            onLog?.(dup.resolution === 'linked'
              ? i18next.t('population:log_child_link_community', { row: rowLabel, id: dup.existingUserId })
              : i18next.t('population:log_child_link_parent', { row: rowLabel, id: dup.existingUserId }))
            childId = dup.existingUserId
          } else {
            const { message, detail } = dup.resolution === 'link-failed'
              ? { message: i18next.t('population:err_link_existing'), detail: '' }
              : parseImportError(res)
            onLog?.(i18next.t('population:log_error', { row: rowLabel, message }))
            onProgress?.(i, rows.length, 'error', message, detail, dup.resolution === 'link-failed' ? 'RELATE_FAILED' : 'DUPLICATE_USER')
            hasImportErrors = true
            continue
          }
        } else if (res?.data?.status === 400) {
          const { message, detail, code } = parseImportError(res)
          onLog?.(i18next.t('population:log_error', { row: rowLabel, message }))
          onProgress?.(i, rows.length, 'error', message, detail, code)
          hasImportErrors = true
          continue
        } else if (!res?.data?.user) {
          onLog?.(i18next.t('population:log_child_create_failed', { row: rowLabel }))
          onProgress?.(i, rows.length, 'error', i18next.t('population:err_child_create_failed'), '', 'CHILD_CREATE_FAILED')
          hasImportErrors = true
          continue
        } else {
          childId = res.data.user.userId
          onLog?.(i18next.t('population:log_child_created', { row: rowLabel, id: childId }))
        }
      }

      onLog?.(i18next.t('population:log_checking_relation', { row: rowLabel }))

      const childFamily: any = await getFamilyRelations(childId, communityId)
      // A parent-child relation may be stored in either direction (this parent
      // as userId or as relatedUserId), so check both endpoints. Matching this
      // specific parentId also lets a child be linked to a second parent.
      const hasParentRelation = Array.isArray(childFamily?.relations) && childFamily.relations.some((r: any) => r.relationTypeId === 2 && (r.relatedUserId === parentId || r.userId === parentId))
      if (!hasParentRelation) {
        onLog?.(i18next.t('population:log_linking_relation', { row: rowLabel }))
        await assignUser({ userId: parentId, communityId, relatedUserId: childId, relationTypeId: 2 })
      } else {
        onLog?.(i18next.t('population:log_relation_exists', { row: rowLabel }))
      }

      imported++
      onLog?.(i18next.t('population:log_completed', { row: rowLabel }))
      onProgress?.(i, rows.length, 'success')
    } catch (err) {
      const { message, detail, code } = parseImportError(err)
      onLog?.(i18next.t('population:log_error', { row: rowLabel, message }))
      onProgress?.(i, rows.length, 'error', message, detail, code)
      hasImportErrors = true
    }
  }

  return { success: !hasImportErrors, imported }
}
